import React, { useEffect, useState } from "react";
import { FiFileText, FiPlus, FiEdit2, FiTrash2, FiExternalLink, FiSave, FiX } from "react-icons/fi";
import { pageAPI } from "../../services/api";
import RichTextEditor from "../../components/admin/RichTextEditor";

// Admin — Gestion des pages CMS (CGU + pages libres).
const EMPTY = {
  slug: "", title: "", titleAr: "", content: "", contentAr: "",
  status: "published", showInFooter: false, showInHeader: false, menuOrder: 0,
  isSystem: false, pageType: "custom", version: ""
};

const AdminPages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // objet en cours d'édition (ou null)
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await pageAPI.adminList();
      setPages((res && res.data && res.data.data) || []);
    } catch (e) {
      setMsg("Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing({ ...EMPTY }); setMsg(""); };
  const startEdit = (p) => { setEditing({ ...EMPTY, ...p, version: p.version || "" }); setMsg(""); };
  const cancel = () => setEditing(null);

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setMsg("");
    try {
      if (editing.id) {
        await pageAPI.adminUpdate(editing.id, editing);
      } else {
        await pageAPI.adminCreate(editing);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setMsg((e && e.response && e.response.data && e.response.data.message) || "Échec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (p.isSystem) return;
    if (!window.confirm(`Supprimer la page « ${p.title || p.slug} » ?`)) return;
    try {
      await pageAPI.adminDelete(p.id);
      await load();
    } catch (e) {
      setMsg((e && e.response && e.response.data && e.response.data.message) || "Suppression impossible.");
    }
  };

  const publicUrl = (p) => (p.slug.startsWith("cgu/") ? `/${p.slug}` : `/p/${p.slug}`);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#fdecf5] text-[#e11d7b]">
            <FiFileText size={20} />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pages du site</h1>
            <p className="text-sm text-gray-500">CGU, mentions légales et pages libres — affichage footer/header.</p>
          </div>
        </div>
        {!editing && (
          <button onClick={startCreate} className="inline-flex items-center gap-2 rounded-lg bg-[#e11d7b] px-4 py-2 text-white font-semibold hover:bg-[#c8166c]">
            <FiPlus size={16} /> Nouvelle page
          </button>
        )}
      </div>

      {msg && <p className="mb-4 text-sm text-red-600">{msg}</p>}

      {editing ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">
              {editing.id ? "Modifier la page" : "Nouvelle page"}
              {editing.isSystem && <span className="ml-2 text-xs font-semibold text-amber-600">(page système)</span>}
            </h2>
            <button onClick={cancel} className="text-gray-400 hover:text-gray-600"><FiX size={20} /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL)</label>
              <input
                value={editing.slug}
                onChange={(e) => set("slug", e.target.value)}
                disabled={editing.isSystem}
                placeholder="ex: a-propos"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
              />
              {editing.isSystem && <p className="mt-1 text-xs text-gray-400">Slug verrouillé (page système).</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ordre dans le menu</label>
              <input type="number" value={editing.menuOrder} onChange={(e) => set("menuOrder", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre (FR)</label>
              <input value={editing.title} onChange={(e) => set("title", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre (AR)</label>
              <input value={editing.titleAr || ""} onChange={(e) => set("titleAr", e.target.value)} dir="rtl"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Contenu (FR)</label>
              <RichTextEditor
                key={`fr-${editing.id || "new"}`}
                value={editing.content || ""}
                onChange={(html) => set("content", html)}
                dir="ltr"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Contenu (AR)</label>
              <RichTextEditor
                key={`ar-${editing.id || "new"}`}
                value={editing.contentAr || ""}
                onChange={(html) => set("contentAr", html)}
                dir="rtl"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select value={editing.status} onChange={(e) => set("status", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="published">Publiée</option>
                <option value="draft">Brouillon</option>
              </select>
            </div>

            {editing.pageType !== "custom" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Version CGU <span className="text-xs text-amber-600">(bumper = re-acceptation forcée)</span>
                </label>
                <input value={editing.version || ""} onChange={(e) => set("version", e.target.value)} placeholder="ex: 2026.2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            )}

            <div className="flex items-center gap-6 md:col-span-2">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={!!editing.showInFooter} onChange={(e) => set("showInFooter", e.target.checked)} className="h-4 w-4 accent-[#e11d7b]" />
                <span className="text-sm text-gray-700">Afficher dans le footer</span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={!!editing.showInHeader} onChange={(e) => set("showInHeader", e.target.checked)} className="h-4 w-4 accent-[#e11d7b]" />
                <span className="text-sm text-gray-700">Afficher dans le header</span>
              </label>
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button onClick={save} disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-[#e11d7b] px-5 py-2.5 text-white font-semibold hover:bg-[#c8166c] disabled:opacity-50">
              <FiSave size={16} /> {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
            <button onClick={cancel} className="rounded-lg border border-gray-300 px-5 py-2.5 text-gray-700 hover:bg-gray-50">Annuler</button>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Titre</th>
                <th className="text-left px-4 py-3 font-semibold">Slug</th>
                <th className="text-left px-4 py-3 font-semibold">Statut</th>
                <th className="text-left px-4 py-3 font-semibold">Emplacement</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Chargement…</td></tr>
              ) : pages.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Aucune page.</td></tr>
              ) : (
                pages.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-3 font-medium text-gray-900">{p.title || "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{p.slug}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${p.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                        {p.status === "published" ? "Publiée" : "Brouillon"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {[p.showInFooter ? "Footer" : null, p.showInHeader ? "Header" : null].filter(Boolean).join(" + ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{p.pageType === "custom" ? "Libre" : "CGU"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <a href={publicUrl(p)} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-700" title="Voir"><FiExternalLink size={16} /></a>
                        <button onClick={() => startEdit(p)} className="text-blue-600 hover:text-blue-800" title="Modifier"><FiEdit2 size={16} /></button>
                        {!p.isSystem && (
                          <button onClick={() => remove(p)} className="text-red-500 hover:text-red-700" title="Supprimer"><FiTrash2 size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPages;
